// Player-Step (Spieler) – Spielereingabe, Teilnehmer-Summary, Team-Zulosung bei Feste Doppel

import React, { useMemo, useCallback, useState, useEffect } from "react";
import {
  Users,
  Info,
  Award,
  UserX,
  Sparkles,
  Check,
  X,
  Ban,
} from "lucide-react";
import { PlayerInput } from "@tennis-matches/ui/tournament-setup";
import {
  __asStr,
  isExcluded,
  hasMissingLkForLkBased,
} from "@tennis-matches/core/utils";

const TEAM_ALLOCATION_LK = "lkBased";
const TEAM_ALLOCATION_MANUAL = "manual";
const TEAM_ALLOCATION_RANDOM = "random";

export function PlayerStep({
  state,
  wizardMode,
  format,
  names,
  setNames,
  teamAllocationMode,
  setTeamAllocationMode,
  manualTeams,
  setManualTeams,
  lkByPlayer,
  setLkByPlayer,
  excludesByPlayer,
  setExcludesByPlayer,
  t,
  onSearchPlayers,
  onAddPlayer,
  onPreparePlayerContact,
  onCreatePlayerContact,
  onPlayerContactResolved,
  onDeletePlayer,
  playerEntryMode = "manual",
  setPlayerEntryMode,
  registrationUrl = "",
  showLkOnSingles = false,
  minRequiredOverride,
  minFieldsOverride,
}) {
  const translate =
    t || (() => (key, vars) => (typeof key === "string" ? key : ""));

  const isDoublesFormat = format === "doubles" || format === "fixedDoubles";
  const minFields = useMemo(
    () => minFieldsOverride ?? (format === "singles" ? 4 : 8),
    [format, minFieldsOverride],
  );
  const minRequired = useMemo(
    () => minRequiredOverride ?? (format === "singles" ? 3 : 4),
    [format, minRequiredOverride],
  );

  const showLkField =
    (format === "fixedDoubles" && teamAllocationMode === TEAM_ALLOCATION_LK) ||
    showLkOnSingles;
  const showTeamAllocation = format === "fixedDoubles";
  const [selectedForPairing, setSelectedForPairing] = useState(null); // number | null
  const [showExcludes, setShowExcludes] = useState(null); // number | null – welcher Spieler-Index hat Ausschluss-Panel offen
  const [savedContactNameKeys, setSavedContactNameKeys] = useState(
    () => new Set(),
  );
  const [linkCopied, setLinkCopied] = useState(false);
  const effectiveEntryMode = ["manual", "link", "hybrid"].includes(
    playerEntryMode,
  )
    ? playerEntryMode
    : "manual";
  const showManualInputs = effectiveEntryMode !== "link";

  // Liste Spieler mit Index (Keys aus names, 1-basiert) und Name
  const players = useMemo(() => {
    if (!names || typeof names !== "object") return [];
    return Object.entries(names)
      .filter(([, v]) => {
        const n = __asStr(v).trim();
        return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
      })
      .map(([k, v]) => ({ index: Number(k), name: __asStr(v).trim() }))
      .sort((a, b) => a.index - b.index);
  }, [names]);

  const filledPlayers = players.length;
  const requiredTeams = Math.floor(filledPlayers / 2);
  const playerIndices = useMemo(() => players.map((p) => p.index), [players]);
  const showMissingLkWarning =
    format === "fixedDoubles" &&
    teamAllocationMode === TEAM_ALLOCATION_LK &&
    filledPlayers >= 2 &&
    hasMissingLkForLkBased(playerIndices, lkByPlayer);

  const getPlayerTeam = useCallback(
    (playerIndex) => {
      const teamIndex = manualTeams.findIndex(
        ([a, b]) => a === playerIndex || b === playerIndex,
      );
      return teamIndex !== -1 ? teamIndex : null;
    },
    [manualTeams],
  );

  const getPartner = useCallback(
    (playerIndex) => {
      for (const [a, b] of manualTeams) {
        if (a === playerIndex) return b;
        if (b === playerIndex) return a;
      }
      return null;
    },
    [manualTeams],
  );

  const handlePlayerClickForPairing = useCallback(
    (index) => {
      const player = players.find((p) => p.index === index);
      if (!player?.name?.trim()) return;

      const existingTeam = getPlayerTeam(index);

      if (existingTeam !== null) {
        setManualTeams((teams) => teams.filter((_, i) => i !== existingTeam));
        setSelectedForPairing(null);
        return;
      }

      if (selectedForPairing === null) {
        setSelectedForPairing(index);
        return;
      }

      if (selectedForPairing === index) {
        setSelectedForPairing(null);
        return;
      }

      if (isExcluded(excludesByPlayer, selectedForPairing, index)) {
        return;
      }

      setManualTeams((teams) => [...teams, [selectedForPairing, index]]);
      setSelectedForPairing(null);
    },
    [players, selectedForPairing, getPlayerTeam, excludesByPlayer],
  );

  // Bei Spieler-Löschung: manualTeams bereinigen (nur Paare behalten, deren Spieler noch einen Namen haben)
  useEffect(() => {
    if (!names || typeof names !== "object") return;
    const validIndices = new Set(
      Object.entries(names)
        .filter(([, v]) => {
          const n = __asStr(v).trim();
          return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
        })
        .map(([k]) => Number(k)),
    );
    setManualTeams((teams) => {
      const next = teams.filter(
        ([a, b]) => validIndices.has(a) && validIndices.has(b),
      );
      if (
        next.length === teams.length &&
        next.every((p, i) => p[0] === teams[i][0] && p[1] === teams[i][1])
      ) {
        return teams;
      }
      return next;
    });
  }, [names]);

  // Ausschlüsse: nur gültige Indizes behalten (Spieler gelöscht → Index aus excludes entfernen)
  useEffect(() => {
    if (!names || typeof names !== "object") return;
    const validIndices = new Set(
      Object.entries(names)
        .filter(([, v]) => {
          const n = __asStr(v).trim();
          return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
        })
        .map(([k]) => Number(k)),
    );
    setExcludesByPlayer((prev) => {
      let changed = false;
      const next = {};
      for (const [key, arr] of Object.entries(prev)) {
        if (!validIndices.has(Number(key))) continue;
        const filtered = (arr || []).filter((i) => validIndices.has(i));
        if (filtered.length !== (arr || []).length) changed = true;
        next[key] = filtered;
      }
      return changed ? next : prev;
    });
  }, [names]);

  const handleExcludeChange = useCallback((playerKey, excludes) => {
    const key = String(playerKey);
    const newSet = new Set(excludes.map(Number).filter(Number.isFinite));
    setExcludesByPlayer((prev) => {
      const next = { ...prev, [key]: [...newSet].sort((a, b) => a - b) };
      const prevList = prev[key] || [];
      const prevSet = new Set(prevList.map(Number));
      for (const other of newSet) {
        const otherKey = String(other);
        const otherList = next[otherKey] ?? prev[otherKey] ?? [];
        const otherSet = new Set(otherList.map(Number));
        if (!otherSet.has(Number(key))) {
          next[otherKey] = [...otherSet, Number(key)].sort((a, b) => a - b);
        }
      }
      for (const other of prevSet) {
        if (newSet.has(other)) continue;
        const otherKey = String(other);
        const otherList = next[otherKey] ?? prev[otherKey] ?? [];
        const filtered = otherList.filter((x) => Number(x) !== Number(key));
        next[otherKey] = filtered.length ? filtered : [];
      }
      return next;
    });
  }, []);

  const unpairedPlayers = useMemo(
    () => players.filter((p) => getPlayerTeam(p.index) === null),
    [players, getPlayerTeam],
  );
  const pairedTeamsCount = manualTeams.length;

  // Calculate actual player count from names
  const actualPlayerCount = useMemo(() => {
    if (!names || typeof names !== "object") return 0;
    return Object.values(names).filter((name) => {
      const n = __asStr(name);
      return n.length > 0 && n !== "(Optional)" && n !== "(optional)";
    }).length;
  }, [names]);

  const handleNamesChange = useCallback(
    (updatedNames) => {
      setNames(updatedNames);
    },
    [setNames],
  );

  const handleSearchPlayers = useCallback(
    async (query) => {
      if (!onSearchPlayers) return [];
      try {
        return await onSearchPlayers(query, 10);
      } catch (_) {
        return [];
      }
    },
    [onSearchPlayers],
  );

  const handlePlayerSave = useCallback(
    async (name, meta = {}) => {
      const rawName = __asStr(name);
      if (!rawName) return;
      const key = rawName.toLowerCase().replace(/\s+/g, " ");
      if (savedContactNameKeys.has(key)) return;

      if (!onPreparePlayerContact || !onCreatePlayerContact) {
        if (onAddPlayer) {
          try {
            await onAddPlayer(rawName);
            setSavedContactNameKeys((prev) => new Set(prev).add(key));
          } catch (_) {}
        }
        return;
      }

      try {
        const prepared = await onPreparePlayerContact(rawName);
        if (!prepared?.ok) return;
        const exact = prepared.exact;
        if (exact?.id) {
          meta.setName?.(exact.name);
          onPlayerContactResolved?.(meta.index, exact);
          setSavedContactNameKeys((prev) => new Set(prev).add(key));
          return;
        }

        const parsed = prepared.parsed;
        if (parsed?.requiresConfirmation && typeof window !== "undefined") {
          const ok = window.confirm(
            translate("club.setup.players.nameConfirm.message", {
              input: rawName,
              firstName: parsed.firstName,
              lastName: parsed.lastName || "--",
            }),
          );
          if (!ok) return;
        }

        if (Array.isArray(prepared.similar) && prepared.similar.length > 0) {
          const similarNames = prepared.similar.map((p) => p.name).join(", ");
          const ok =
            typeof window === "undefined" ||
            window.confirm(
              translate("club.setup.players.similarConfirm.message", {
                input: rawName,
                matches: similarNames,
              }),
            );
          if (!ok) return;
        }

        const created = await onCreatePlayerContact(parsed);
        if (created?.ok && created.data?.id) {
          meta.setName?.(created.data.name);
          onPlayerContactResolved?.(meta.index, created.data);
          setSavedContactNameKeys((prev) => new Set(prev).add(key));
        }
      } catch (_) {}
    },
    [
      onAddPlayer,
      onCreatePlayerContact,
      onPlayerContactResolved,
      onPreparePlayerContact,
      savedContactNameKeys,
      translate,
    ],
  );

  const handlePlayerDelete = useCallback(
    async (playerId) => {
      if (onDeletePlayer) {
        try {
          await onDeletePlayer(playerId);
        } catch (e) {
          throw e;
        }
      }
    },
    [onDeletePlayer],
  );

  const handlePlayerSelect = useCallback(
    (suggestion, meta = {}) => {
      if (!suggestion?.id) return;
      onPlayerContactResolved?.(meta.index, suggestion);
      setSavedContactNameKeys((prev) =>
        new Set(prev).add(
          __asStr(suggestion.name).toLowerCase().replace(/\s+/g, " "),
        ),
      );
    },
    [onPlayerContactResolved],
  );

  const handleCopyRegistrationUrl = useCallback(async () => {
    if (!registrationUrl || typeof navigator === "undefined") return;
    try {
      await navigator.clipboard?.writeText(registrationUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1800);
    } catch (_) {}
  }, [registrationUrl]);

  const isExpert = wizardMode === "expert";

  return (
    <div className="tm-wizard-player-step">
      {/* v0 Summary Card (Teilnehmer) */}
      <div className="tm-wizard-summary-card tm-wizard-summary-card--players">
        <div className="tm-wizard-summary-card__left">
          <span className="tm-wizard-icon-bubble tm-wizard-icon-bubble--green">
            <Users className="tm-wizard-icon" />
          </span>
          <div className="tm-wizard-summary-card__text">
            <span className="tm-wizard-summary-card__label">
              {translate("settings.wizard.workflow.participants") ||
                "Teilnehmer"}
            </span>
            <span className="tm-wizard-summary-card__value">
              {actualPlayerCount}
            </span>
          </div>
        </div>
        <div className="tm-wizard-summary-card__right">
          <span className="tm-wizard-summary-card__min">
            {translate("settings.wizard.minPlayers", { count: minRequired }) ||
              `Min. ${minRequired} Spieler`}
          </span>
          {actualPlayerCount < minRequired ? (
            <span className="tm-wizard-summary-card__warning">
              {translate("settings.players.needed", {
                count: minRequired - actualPlayerCount,
              })}
            </span>
          ) : (
            <span className="tm-wizard-summary-card__ready">
              {translate("settings.wizard.ready") || "Bereit"}
            </span>
          )}
        </div>
      </div>

      <section className="tm-wizard-card tm-wizard-card--mb tm-wizard-section-card">
        <div className="tm-wizard-team-allocation__head">
          <span
            className="tm-wizard-icon-bubble tm-wizard-icon-bubble--accent tm-wizard-team-allocation__icon"
            aria-hidden="true"
          >
            <Users className="tm-wizard-icon" />
          </span>
          <div className="tm-wizard-team-allocation__head-text">
            <h3 className="tm-wizard-team-allocation__title">
              {translate("club.setup.players.entryMode.title")}
            </h3>
            <p className="tm-wizard-team-allocation__question muted">
              {translate("club.setup.players.entryMode.hint")}
            </p>
          </div>
        </div>
        <div className="tm-wizard-team-allocation__options">
          {["manual", "link", "hybrid"].map((mode) => (
            <button
              key={mode}
              type="button"
              className={`tm-wizard-card tm-wizard-card--selectable tm-wizard-team-allocation__option ${effectiveEntryMode === mode ? "tm-wizard-card--selected" : ""}`}
              onClick={() => setPlayerEntryMode?.(mode)}
            >
              <div className="tm-wizard-card__top">
                {effectiveEntryMode === mode ? (
                  <span className="tm-wizard-card__check" aria-hidden="true">
                    <Check className="tm-wizard-icon tm-wizard-icon--sm" />
                  </span>
                ) : null}
              </div>
              <div className="tm-wizard-card__text">
                <span className="tm-wizard-card__value">
                  {translate(`club.setup.players.entryMode.${mode}`)}
                </span>
                <span className="tm-wizard-card__desc">
                  {translate(`club.setup.players.entryMode.${mode}.desc`)}
                </span>
              </div>
            </button>
          ))}
        </div>
      </section>

      {effectiveEntryMode !== "manual" ? (
        <section className="tm-wizard-card tm-wizard-card--mb tm-wizard-hint-box tm-wizard-hint-box--ok">
          <div className="tm-wizard-hint-box__header">
            <Info className="tm-wizard-icon tm-wizard-icon--sm" aria-hidden />
            <span className="tm-wizard-hint-box__title">
              {translate("club.setup.players.registrationLink.title")}
            </span>
          </div>
          <p className="tm-wizard-hint-box__text">
            {translate(
              `club.setup.players.registrationLink.${effectiveEntryMode}.hint`,
            )}
          </p>
          {registrationUrl ? (
            <div className="tm-wizard-v0__action-row">
              <input
                type="text"
                readOnly
                value={registrationUrl}
                aria-label={translate(
                  "club.setup.players.registrationLink.aria",
                )}
                className="tm-wizard-v0__input"
              />
              <button
                type="button"
                className="tm-wizard-v0__btn"
                onClick={handleCopyRegistrationUrl}
              >
                {linkCopied
                  ? translate("club.setup.players.registrationLink.copied")
                  : translate("club.setup.players.registrationLink.copy")}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {/* Team-Zulosung: bei Doppel/Feste Doppel, zwischen Teilnehmer und Name 1-n */}
      {showManualInputs && showTeamAllocation ? (
        <div className="tm-wizard-card tm-wizard-team-allocation tm-wizard-card--mb">
          <div className="tm-wizard-team-allocation__head">
            <span
              className="tm-wizard-icon-bubble tm-wizard-icon-bubble--accent tm-wizard-team-allocation__icon"
              aria-hidden="true"
            >
              <Sparkles className="tm-wizard-icon" />
            </span>
            <div className="tm-wizard-team-allocation__head-text">
              <h3 className="tm-wizard-team-allocation__title">
                {translate("settings.wizard.teamAllocation")}
              </h3>
              <p className="tm-wizard-team-allocation__question muted">
                {translate("settings.wizard.teamAllocation.question")}
              </p>
            </div>
          </div>
          <div className="tm-wizard-team-allocation__options">
            <button
              type="button"
              className={`tm-wizard-card tm-wizard-card--selectable tm-wizard-team-allocation__option ${teamAllocationMode === TEAM_ALLOCATION_MANUAL ? "tm-wizard-card--selected" : ""}`}
              onClick={() => setTeamAllocationMode(TEAM_ALLOCATION_MANUAL)}
            >
              <div className="tm-wizard-card__top">
                {teamAllocationMode === TEAM_ALLOCATION_MANUAL && (
                  <span className="tm-wizard-card__check" aria-hidden="true">
                    <Check className="tm-wizard-icon tm-wizard-icon--sm" />
                  </span>
                )}
              </div>
              <div className="tm-wizard-card__text">
                <span className="tm-wizard-card__value">
                  {translate("settings.wizard.teamAllocation.manual")}
                </span>
                <span className="tm-wizard-card__desc">
                  {translate("settings.wizard.teamAllocation.manual.desc")}
                </span>
              </div>
            </button>
            {format !== "fixedDoubles" && (
              <button
                type="button"
                className={`tm-wizard-card tm-wizard-card--selectable tm-wizard-team-allocation__option ${teamAllocationMode === TEAM_ALLOCATION_RANDOM ? "tm-wizard-card--selected" : ""}`}
                onClick={() => setTeamAllocationMode(TEAM_ALLOCATION_RANDOM)}
              >
                <div className="tm-wizard-card__top">
                  {teamAllocationMode === TEAM_ALLOCATION_RANDOM && (
                    <span className="tm-wizard-card__check" aria-hidden="true">
                      <Check className="tm-wizard-icon tm-wizard-icon--sm" />
                    </span>
                  )}
                </div>
                <div className="tm-wizard-card__text">
                  <span className="tm-wizard-card__value">
                    {translate("settings.wizard.teamAllocation.random")}
                  </span>
                  <span className="tm-wizard-card__desc">
                    {translate("settings.wizard.teamAllocation.random.desc")}
                  </span>
                </div>
              </button>
            )}
            <button
              type="button"
              className={`tm-wizard-card tm-wizard-card--selectable tm-wizard-team-allocation__option ${teamAllocationMode === TEAM_ALLOCATION_LK ? "tm-wizard-card--selected" : ""}`}
              onClick={() => setTeamAllocationMode(TEAM_ALLOCATION_LK)}
            >
              <div className="tm-wizard-card__top">
                {teamAllocationMode === TEAM_ALLOCATION_LK && (
                  <span className="tm-wizard-card__check" aria-hidden="true">
                    <Check className="tm-wizard-icon tm-wizard-icon--sm" />
                  </span>
                )}
                <span className="tm-wizard-badge-tag tm-wizard-badge-tag--recommended tm-wizard-card__badge">
                  {translate("settings.wizard.recommended")}
                </span>
              </div>
              <div className="tm-wizard-card__text">
                <span className="tm-wizard-card__value">
                  {translate("settings.wizard.teamAllocation.lkBased")}
                </span>
                <span className="tm-wizard-card__desc">
                  {translate("settings.wizard.teamAllocation.lkBased.desc")}
                </span>
              </div>
            </button>
          </div>
        </div>
      ) : null}

      {/* Manuelle Team-Zulosung: nur bei Feste Doppel + Manuell */}
      {showManualInputs &&
        format === "fixedDoubles" &&
        teamAllocationMode === TEAM_ALLOCATION_MANUAL &&
        filledPlayers >= 2 && (
          <section className="tm-wizard-manual-teams tm-wizard-card tm-wizard-card--mb">
            <div className="tm-wizard-manual-teams__head">
              <div className="tm-wizard-manual-teams__head-left">
                <span
                  className="tm-wizard-icon-bubble tm-wizard-icon-bubble--green tm-wizard-manual-teams__icon"
                  aria-hidden="true"
                >
                  <Users className="tm-wizard-icon" />
                </span>
                <div className="tm-wizard-manual-teams__head-text">
                  <h3 className="tm-wizard-manual-teams__title">
                    {translate("settings.wizard.manualTeams.title")}
                  </h3>
                  <p className="tm-wizard-manual-teams__hint">
                    {translate("settings.wizard.manualTeams.hint")}
                  </p>
                </div>
              </div>
              <div className="tm-wizard-manual-teams__counter">
                <span className="tm-wizard-manual-teams__counter-value">
                  {pairedTeamsCount}/{requiredTeams}
                </span>
                <span className="tm-wizard-manual-teams__counter-label">
                  {translate("settings.wizard.manualTeams.formed")}
                </span>
              </div>
            </div>

            {manualTeams.length > 0 && (
              <div className="tm-wizard-manual-teams__list-wrap">
                <p className="tm-wizard-manual-teams__list-label">
                  {translate("settings.wizard.manualTeams.formedList")}
                </p>
                <div className="tm-wizard-manual-teams__list">
                  {manualTeams.map(([a, b], teamIndex) => (
                    <div
                      key={teamIndex}
                      className="tm-wizard-manual-teams__team-row"
                    >
                      <div className="tm-wizard-manual-teams__team-left">
                        <span className="tm-wizard-manual-teams__team-badge">
                          {teamIndex + 1}
                        </span>
                        <span className="tm-wizard-manual-teams__team-names">
                          {__asStr(names?.[String(a)] ?? "")} &{" "}
                          {__asStr(names?.[String(b)] ?? "")}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setManualTeams((teams) =>
                            teams.filter((_, i) => i !== teamIndex),
                          )
                        }
                        className="tm-wizard-manual-teams__remove"
                        aria-label={
                          translate("settings.wizard.manualTeams.removeTeam") ||
                          "Team entfernen"
                        }
                      >
                        <X className="tm-wizard-icon tm-wizard-icon--sm" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {unpairedPlayers.length > 0 && (
              <div className="tm-wizard-manual-teams__chips-wrap">
                <p className="tm-wizard-manual-teams__chips-label">
                  {selectedForPairing !== null
                    ? translate("settings.wizard.manualTeams.selectPartner", {
                        name: __asStr(
                          names?.[String(selectedForPairing)] ?? "",
                        ),
                      })
                    : translate("settings.wizard.manualTeams.available")}
                </p>
                <div className="tm-wizard-manual-teams__chips">
                  {unpairedPlayers.map((player) => {
                    const isSelected = selectedForPairing === player.index;
                    const isExcludedPartner =
                      selectedForPairing !== null &&
                      isExcluded(
                        excludesByPlayer,
                        selectedForPairing,
                        player.index,
                      );
                    const chipClass = isSelected
                      ? "tm-wizard-manual-teams__chip tm-wizard-manual-teams__chip--selected"
                      : isExcludedPartner
                        ? "tm-wizard-manual-teams__chip tm-wizard-manual-teams__chip--excluded"
                        : selectedForPairing !== null
                          ? "tm-wizard-manual-teams__chip tm-wizard-manual-teams__chip--available"
                          : "tm-wizard-manual-teams__chip";
                    return (
                      <button
                        key={player.index}
                        type="button"
                        onClick={() =>
                          handlePlayerClickForPairing(player.index)
                        }
                        className={chipClass}
                        disabled={isExcludedPartner}
                        title={
                          isExcludedPartner
                            ? translate(
                                "settings.wizard.manualTeams.cannotPair",
                              )
                            : undefined
                        }
                        aria-disabled={isExcludedPartner}
                      >
                        {player.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {pairedTeamsCount === requiredTeams && requiredTeams > 0 ? (
              <div className="tm-wizard-manual-teams__status tm-wizard-manual-teams__status--done">
                <Check className="tm-wizard-icon tm-wizard-icon--sm" />
                <span>{translate("settings.wizard.manualTeams.allDone")}</span>
              </div>
            ) : null}
            {filledPlayers % 2 !== 0 ? (
              <p className="tm-wizard-manual-teams__status tm-wizard-manual-teams__status--odd">
                <Info className="tm-wizard-icon tm-wizard-icon--sm" />
                {translate("settings.wizard.manualTeams.oddPlayers")}
              </p>
            ) : null}
          </section>
        )}

      {/* Player Input (Name 1-n) */}
      {showManualInputs ? (
        <div className="tm-wizard-player-input">
          <PlayerInput
            names={names}
            onNamesChange={handleNamesChange}
            minFields={minFields}
            format={format}
            minRequired={minRequired}
            showLkField={showLkField}
            lkValues={lkByPlayer}
            onLkChange={setLkByPlayer}
            t={translate}
            hintText={translate("settings.playerNamesHint")}
            showPlayerCount={false}
            onSearchPlayers={handleSearchPlayers}
            onPlayerSave={handlePlayerSave}
            onPlayerSelect={handlePlayerSelect}
            onPlayerDelete={onDeletePlayer ? handlePlayerDelete : null}
            autoSaveOnBlur
            showExcludeUI={isExpert && isDoublesFormat}
            excludesByPlayer={excludesByPlayer}
            onExcludeChange={handleExcludeChange}
            showExcludesKey={showExcludes != null ? String(showExcludes) : null}
            onShowExcludesChange={(key) =>
              setShowExcludes(key != null ? Number(key) : null)
            }
            excludeButtonIcon={
              <Ban className="tm-wizard-icon tm-wizard-icon--sm" />
            }
            showAddPlayerButton
            showHintBox={false}
          />
        </div>
      ) : null}

      {/* LK pro Spieler: nur bei Feste Doppel + LK-basierte Team-Zulosung */}
      {showManualInputs && isExpert && showLkField ? (
        <div className="tm-wizard-card tm-wizard-card--mb tm-wizard-section-card tm-wizard-hint-box tm-wizard-hint-box--ok">
          <div className="tm-wizard-hint-box__header">
            <Award className="tm-wizard-icon tm-wizard-icon--sm" aria-hidden />
            <span className="tm-wizard-hint-box__title">
              {translate("settings.wizard.lkPerPlayer")}
            </span>
          </div>
          <p className="tm-wizard-hint-box__text">
            {translate(
              showLkOnSingles
                ? "club.seeding.lkPerPlayer.hint"
                : "settings.wizard.lkPerPlayer.hint",
            )}
          </p>
          {showMissingLkWarning ? (
            <p
              className="tm-wizard-hint-box__text tm-wizard-hint-box__text--warning"
              role="status"
            >
              {translate(
                "settings.wizard.teamAllocation.lkBased.missingLkWarning",
              )}
            </p>
          ) : null}
        </div>
      ) : null}
      {/* Inline-Warnung fehlende LK: auch im Simple-Modus bei Feste Doppel + LK */}
      {showManualInputs && !isExpert && showMissingLkWarning ? (
        <div
          className="tm-wizard-card tm-wizard-card--mb tm-wizard-hint-box tm-wizard-hint-box--warn"
          role="status"
        >
          <p className="tm-wizard-hint-box__text">
            {translate(
              "settings.wizard.teamAllocation.lkBased.missingLkWarning",
            )}
          </p>
        </div>
      ) : null}

      {/* Ausschlüsse-Box: nur bei Doppel/Feste Doppel + Expert (bei Einzel nicht relevant) */}
      {showManualInputs && isExpert && format !== "singles" ? (
        <div className="tm-wizard-card tm-wizard-card--mb tm-wizard-section-card tm-wizard-hint-box tm-wizard-hint-box--err">
          <div className="tm-wizard-hint-box__header">
            <UserX className="tm-wizard-icon tm-wizard-icon--sm" aria-hidden />
            <span className="tm-wizard-hint-box__title">
              {translate("settings.wizard.exclusion")}
            </span>
          </div>
          <p className="tm-wizard-hint-box__text">
            {translate("settings.wizard.exclusion.hint")}
          </p>
        </div>
      ) : null}
    </div>
  );
}
